/**
 * MarkdownEditor — controlled textarea with a lightweight formatting toolbar.
 *
 * Purpose: produce markdown source for legal documents and similar content.
 * Flow:
 *   - Maintains a ref to the underlying textarea.
 *   - Toolbar buttons insert markdown around the current selection
 *     (or at the caret position if no selection), then re-focuses the field.
 *   - Calls `onChange` with the resulting string for parent state.
 *
 * Direction:
 *   - `dir` prop (`ltr` | `rtl`) controls writing direction of the textarea.
 *     Defaults to document direction; required for Arabic content.
 *
 * @layer-1 — shared, no feature imports
 */
import { forwardRef, useLayoutEffect, useRef } from "react";
import {
  Bold,
  Heading1,
  Heading2,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { getEffectiveDir } from "@/shared/lib/direction";

interface MarkdownEditorProps {
  value: string;
  onChange: (next: string) => void;
  /** Writing direction; defaults to document direction. */
  dir?: "ltr" | "rtl";
  /** Placeholder shown while empty. */
  placeholder?: string;
  /** Minimum row count for the textarea. */
  minRows?: number;
  /** Pass-through className for the outer wrapper. */
  className?: string;
  /** Optional disabled state — disables toolbar and textarea. */
  disabled?: boolean;
  /**
   * When true, strips the outer border/background/radius so the editor
   * flows seamlessly inside a parent pane that already provides a container.
   */
  embedded?: boolean;
  /**
   * When true, the textarea grows to fit its content instead of filling the
   * parent height — height tracks `scrollHeight`, with `minRows` as a floor.
   */
  autoGrow?: boolean;
}

interface ToolbarButton {
  icon: typeof Bold;
  label: string;
  /** Token inserted before the selection. */
  prefix: string;
  /** Token inserted after the selection. */
  suffix: string;
  /** If true, applied as a line prefix instead of around selection. */
  linePrefix?: boolean;
}

const BUTTONS: ToolbarButton[] = [
  { icon: Heading1, label: "H1", prefix: "# ", suffix: "", linePrefix: true },
  { icon: Heading2, label: "H2", prefix: "## ", suffix: "", linePrefix: true },
  { icon: Bold, label: "Bold", prefix: "**", suffix: "**" },
  { icon: Italic, label: "Italic", prefix: "*", suffix: "*" },
  { icon: List, label: "Bullet list", prefix: "- ", suffix: "", linePrefix: true },
  { icon: ListOrdered, label: "Numbered list", prefix: "1. ", suffix: "", linePrefix: true },
  { icon: Quote, label: "Quote", prefix: "> ", suffix: "", linePrefix: true },
  { icon: LinkIcon, label: "Link", prefix: "[", suffix: "](https://)" },
];

/**
 * Apply a toolbar button to the current selection inside `textarea`.
 * Returns the new value and the new caret/selection range.
 */
function applyButton(
  textarea: HTMLTextAreaElement,
  btn: ToolbarButton,
): { value: string; selectionStart: number; selectionEnd: number } {
  const { value, selectionStart: s, selectionEnd: e } = textarea;
  if (btn.linePrefix) {
    // Apply prefix to the start of every selected line (or current line).
    const lineStart = value.lastIndexOf("\n", s - 1) + 1;
    const before = value.slice(0, lineStart);
    const middle = value.slice(lineStart, e);
    const after = value.slice(e);
    const transformed = middle
      .split("\n")
      .map((l) => (l.startsWith(btn.prefix) ? l : btn.prefix + l))
      .join("\n");
    const nextValue = before + transformed + after;
    return {
      value: nextValue,
      selectionStart: before.length,
      selectionEnd: before.length + transformed.length,
    };
  }
  const selected = value.slice(s, e) || "text";
  const insert = btn.prefix + selected + btn.suffix;
  const nextValue = value.slice(0, s) + insert + value.slice(e);
  return {
    value: nextValue,
    selectionStart: s + btn.prefix.length,
    selectionEnd: s + btn.prefix.length + selected.length,
  };
}

export const MarkdownEditor = forwardRef<HTMLTextAreaElement, MarkdownEditorProps>(
  function MarkdownEditor(
    {
      value,
      onChange,
      dir,
      placeholder,
      minRows = 18,
      className,
      disabled = false,
      embedded = false,
      autoGrow = false,
    },
    externalRef,
  ) {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const ref = (externalRef as React.RefObject<HTMLTextAreaElement> | null) ?? internalRef;
    const effectiveDir = dir ?? getEffectiveDir();

    // Auto-grow: resize the textarea to fit its content on every value change.
    useLayoutEffect(() => {
      if (!autoGrow) return;
      const node = ref.current;
      if (!node) return;
      node.style.height = "auto";
      node.style.height = `${node.scrollHeight}px`;
    }, [value, autoGrow, ref]);

    const handleButton = (btn: ToolbarButton) => {
      const node = ref.current;
      if (!node) return;
      const result = applyButton(node, btn);
      onChange(result.value);
      // Restore selection on next tick so the controlled value has settled.
      requestAnimationFrame(() => {
        node.focus();
        node.setSelectionRange(result.selectionStart, result.selectionEnd);
      });
    };

    return (
      <div
        dir={effectiveDir}
        className={cn(
          "flex flex-col",
          embedded
            ? autoGrow
              ? ""
              : "h-full"
            : "rounded-2xl border border-[var(--border)] bg-[var(--card)]",
          className,
        )}
      >
        {/* Toolbar */}
        <div
          className={cn(
            "flex flex-wrap items-center gap-1 px-3 py-2 shrink-0",
            "border-b border-[var(--border-light)]",
          )}
        >
          {BUTTONS.map((btn) => {
            const Icon = btn.icon;
            return (
              <button
                key={btn.label}
                type="button"
                onClick={() => handleButton(btn)}
                aria-label={btn.label}
                disabled={disabled}
                className={cn(
                  "size-8 inline-flex items-center justify-center rounded-lg",
                  "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                  "hover:bg-[var(--surface)] transition-colors",
                  "disabled:opacity-40 disabled:cursor-not-allowed",
                )}
              >
                <Icon size={16} strokeWidth={1.75} />
              </button>
            );
          })}
        </div>

        {/* Editor — fills remaining height when embedded */}
        <textarea
          ref={ref}
          value={value}
          onChange={(ev) => onChange(ev.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          dir={effectiveDir}
          rows={embedded && !autoGrow ? undefined : minRows}
          style={{ ...(autoGrow && { minHeight: "8rem" }) }}
          className={cn(
            "block w-full bg-transparent",
            "px-4 py-3 text-sm leading-relaxed font-mono",
            "outline-none placeholder:text-[var(--text-tertiary)]",
            "text-[var(--text-primary)]",
            embedded
              ? autoGrow
                ? "resize-none overflow-hidden"
                : "flex-1 resize-none min-h-0"
              : "resize-y rounded-b-2xl",
          )}
          spellCheck={false}
        />
      </div>
    );
  },
);
